import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, MutableRefObject, useRef } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { IcArrowLeft16Icon, IcArrowRight16Icon, IcCloseIcon } from '@gem/icons';
import { ICommonPopupsProps, Popup } from '@gem/popups';
import { Button, customScrollStyles, IconButton, IconViewSizes } from '@gem/ui-kit';

import { useSpaceDemoPopupPresenter } from './SpaceDemoPopup.presenter';
import styles from './SpaceDemoPopup.styles.m.css';

const sliderTransitions = {
  enter: styles.slideEnter,
  enterActive: styles.slideEnterActive,
  exit: styles.slideExit,
  exitActive: styles.slideExitActive,
};

export const SpaceDemoPopup: FC<ICommonPopupsProps> = observer(({ popupInstance }) => {
  const presenter = useSpaceDemoPopupPresenter(popupInstance);
  const nodeRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  return (
    <Popup
      className={styles.popup}
      isOpened={true}
      onClose={presenter.onSkipOnboarding}
    >
      <SwitchTransition>
        <CSSTransition
          classNames={sliderTransitions}
          nodeRef={nodeRef}
          key={presenter.currentSlideIndex}
          addEndListener={(done): void =>
            nodeRef.current?.addEventListener('transitionend', done, false)
          }
        >
          <div
            className={classNames(styles.slide, customScrollStyles.container)}
            ref={nodeRef}
          >
            <img
              className={styles.image}
              src={presenter.slide.image}
              alt=""
            />

            <h3 className={styles.title}>{presenter.slide.title}</h3>

            <div className={styles.description}>{presenter.slide.description}</div>

            {presenter.slide.buttonTitle && (
              <Button
                className={styles.action}
                size="10"
                theme="primary-white"
                onClick={presenter.slide.onButtonClick}
              >
                {presenter.slide.buttonTitle}
              </Button>
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>

      <div className={styles.stepper}>
        {presenter.slides.map((stepConfig, index) => (
          <button
            className={classNames(styles.step, {
              [styles.active]: index === presenter.currentSlideIndex,
            })}
            key={stepConfig.title}
            type="button"
            onClick={(): void => presenter.onSelectSlide(index)}
          >
            <div className={styles.stepPin}></div>
          </button>
        ))}
      </div>

      {presenter.isShowPrevButton && (
        <IconButton
          className={styles.leftButton}
          Icon={IcArrowLeft16Icon}
          size={IconViewSizes.Big}
          iconSize={IconViewSizes.Medium}
          theme="23"
          onClick={presenter.onPrevSlide}
        />
      )}

      {presenter.isShowNextButton && (
        <IconButton
          className={styles.rightButton}
          Icon={IcArrowRight16Icon}
          size={IconViewSizes.Big}
          iconSize={IconViewSizes.Medium}
          theme="23"
          onClick={presenter.onNextSlide}
        />
      )}

      <IconButton
        className={styles.close}
        Icon={IcCloseIcon}
        theme="21"
        onClick={presenter.onSkipOnboarding}
      />
    </Popup>
  );
});
